import {allRouteDefinitions} from 'common/src/modules/routing/routing.config';
import {RouteKey} from 'common/src/modules/routing/routing.enums';
import routingService from 'common/src/modules/routing/routing.service';
import {AppRouteParams, AppRouteQueryParams} from 'common/src/modules/routing/routing.types';
import {RootState} from 'common/src/redux/store';
import defaultRouteFormatter from 'common/src/modules/routing/utils/defaultRouteFormatter';
import {Caller, ToolUtils} from 'common/src/modules/shared/copilot/copilot.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import appendCustomCallerToCallChain from 'common/src/modules/shared/copilot/utils/creators/appendCustomCallerToCallChain';
import {promptToRouteResponseSchema} from '../../generalNavigationTool.schemas';
import {PromptToRouteResult} from '../../generalNavigationTool.types';
import buildPromptToRouteSystemPrompt from './utils/buildPromptToRouteSystemPrompt';
import formatRouteDefs from './utils/formatRouteDefs';
import promptToRouteResultFailure from './utils/promptToRouteResultFailure';

async function promptToRoute(
  prompt: string,
  toolUtils: ToolUtils,
  callChain: Caller[],
  getState: () => RootState,
): Promise<PromptToRouteResult> {
  const currentLocation = _formatCurrentLocationForPrompt(getState);
  const systemPrompt = buildPromptToRouteSystemPrompt(
    currentLocation,
    formatRouteDefs(allRouteDefinitions),
  );

  const response = await toolUtils.invokeStandaloneLLMCall({
    messages: [openAISystemMessage(systemPrompt), openAIUserMessage(prompt)],
    responseSchema: promptToRouteResponseSchema,
    callChain: appendCustomCallerToCallChain('promptToRoute', callChain),
  });

  if (!response.success) {
    const {reasoning, missingInfo} = response;

    return promptToRouteResultFailure({
      reasoning,
      missingInfo,
    });
  }

  const {routeKey, reasoning, confidence} = response;
  const routeDef = allRouteDefinitions[routeKey as RouteKey];

  if (!routeDef || !routeKey) {
    return promptToRouteResultFailure({
      reasoning: `Route not found: ${routeKey}`,
      missingInfo: `The requested route "${routeKey}" was not found in available routes`,
    });
  }

  // Convert array of {key, value} to Record format
  const pathParamValues: AppRouteParams = {};
  response.pathParamValues.forEach(({key, value}) => {
    pathParamValues[key] = value;
  });

  const queryParamValues: AppRouteQueryParams = {};
  response.queryParamValues.forEach(({key, value}) => {
    queryParamValues[key] = value;
  });

  return {
    success: true,
    reasoning,
    pathParamValues,
    queryParamValues,
    confidence,
    routeKey,
    missingInfo: '',
  };
}

function _formatCurrentLocationForPrompt(getState: () => RootState): string | null {
  const currentRoute = routingService.getCurrentRouteWithParams();

  if (!currentRoute) {
    return null;
  }

  const {routeKey} = currentRoute;
  const routeDef = routingService.getRouteDefinition(routeKey);

  if (!routeDef) {
    return null;
  }

  const routeFormatter = routeDef.formatter ?? defaultRouteFormatter;
  const params = routeFormatter({routeDef, route: currentRoute, getState});

  return `Page: ${routeDef.displayName}\nRoute Key: ${routeKey}${params}`;
}

export default promptToRoute;
